import { useEffect, useState, lazy, Suspense } from "react";
import { Link } from "wouter";
import { RefreshCw } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useDashboardOverview } from "@/hooks/useDashboardOverview";
import { useDashboardOrigination } from "@/hooks/useDashboardOrigination";
import { useBoard } from "@/components/dashboard-grid/useBoard";
import { BoardControls } from "@/components/dashboard-grid/BoardControls";
import { OperationsBoard, useOpsWidgets } from "@/components/dashboard/OperationsBoard";
import { GetMobileAppButton } from "@/components/GetMobileAppButton";

const FinancialsTab = lazy(() => import("@/components/financials/FinancialsTab"));

function greeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  if (h >= 17 && h < 21) return "Good evening";
  return "Working late";
}

function updatedAgo(ts: number): string {
  if (!ts) return "";
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
}

export default function Dashboard({ tab = "operations" }: { tab?: "operations" | "financials" }) {
  const { user, canViewFinancials } = useAuth();
  const firstName = (user as any)?.firstName || user?.name?.split(" ")[0] || "there";
  const overview = useDashboardOverview();
  const origination = useDashboardOrigination();
  const board = useBoard("ops", !!canViewFinancials);
  const widgets = useOpsWidgets(!!canViewFinancials, user?.name ?? null, board.layout.items.map((i) => i.id));
  const isError = overview.isError || origination.isError;
  const isFetching = overview.isFetching || origination.isFetching;
  const firstLoadFailed = (overview.isError && !overview.data) || (origination.isError && !origination.data);
  const refetch = () => { overview.refetch(); origination.refetch(); };

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  return (
    <AppLayout title="Dashboard" fill>
      <div className="flex flex-col gap-3 lg:flex-1 lg:min-h-0 page-enter">
        <header className="flex items-center gap-4 flex-wrap shrink-0">
          <h1 className="text-lg font-semibold tracking-tight text-foreground">{greeting()}, {firstName}</h1>
          {canViewFinancials && (
            <nav className="flex gap-0.5 rounded-md bg-muted p-0.5">
              {([["operations", "Operations", "/"], ["financials", "Financials", "/financials"]] as const).map(([key, label, href]) => (
                <Link key={key} href={href}
                  className={cn("px-3 py-1 rounded text-sm font-medium transition-colors",
                    tab === key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}>
                  {label}
                </Link>
              ))}
            </nav>
          )}
          <div className="ml-auto flex items-center gap-3">
            {tab === "operations" && (
              <>
                <BoardControls board="ops" layout={board.layout} widgets={widgets} editing={board.editing}
                  onStart={board.startEditing} onDone={board.done} onReset={board.reset} onAdd={board.add} />
                <button onClick={refetch} disabled={isFetching}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60"
                  title="Refresh dashboard">
                  <RefreshCw className={cn("w-3.5 h-3.5", isFetching && "animate-spin")} />
                  {isFetching ? "Refreshing…" : overview.dataUpdatedAt ? `Updated ${updatedAgo(overview.dataUpdatedAt)}` : "Refresh"}
                </button>
              </>
            )}
            <GetMobileAppButton />
          </div>
        </header>

        {tab === "operations" && (
          <>
            {isError && (
              <div role="alert" className="shrink-0 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm flex items-center gap-3">
                <p className="flex-1">{firstLoadFailed
                  ? "Couldn't load the dashboard. Deal counts and prospects are unavailable."
                  : "Dashboard refresh failed. Showing previously loaded data; it may be out of date."}</p>
                <button type="button" onClick={refetch} disabled={isFetching} className="font-medium underline disabled:opacity-60">
                  {isFetching ? "Retrying…" : "Retry"}
                </button>
              </div>
            )}
            {!firstLoadFailed && (board.ready ? <OperationsBoard board={board} widgets={widgets} /> : <Skeleton className="flex-1 min-h-[24rem] rounded-lg" />)}
          </>
        )}

        {tab === "financials" && (
          <Suspense fallback={<Skeleton className="flex-1 min-h-[24rem] rounded-lg" />}>
            <FinancialsTab />
          </Suspense>
        )}
      </div>
    </AppLayout>
  );
}
